import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Save, X, Layers, AlertCircle } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';

// Bulk-generate N units of one catalog-backed unit type. Numbers =
// `${prefix}${n}` for n in [start, start+qty), OR an explicit list of
// unit_numbers. Architecture A: unit_type_id is REQUIRED; rate and
// display name inherit from the catalog (operator can override the
// rate for this batch).
const UnitBulkForm = () => {
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unitTypes, setUnitTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  const [form, setForm] = useState({
    prefix: '',
    start_number: '',
    quantity: 10,
    unit_type_id: '',
    unit_type: '',          // legacy display cache — mirrored from picked type
    unit_category: 'enclosed',
    monthly_rate: '',       // empty = inherit; non-empty = batch override
    width: '',
    length: '',
    notes: '',
  });

  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    (async () => {
      setLoadingTypes(true);
      try {
        const res = await apiClient.get(
          `/api/facilities/${facilityId}/unit-types/`
        );
        if (!cancelled) setUnitTypes(res.data || []);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load unit types:', err);
          setError('Could not load unit types for this facility.');
        }
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [facilityId]);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    const selected = unitTypes.find((t) => t._id === typeId);
    setForm({
      ...form,
      unit_type_id: typeId,
      unit_type: selected?.name || '',
      unit_category: selected?.category || form.unit_category,
      monthly_rate:
        selected?.monthly_rate != null
          ? String(selected.monthly_rate)
          : '',
    });
  };

  const noTypesDefined = !loadingTypes && unitTypes.length === 0;

  const qty = Math.max(0, Math.min(500, parseInt(form.quantity, 10) || 0));
  const startRaw = String(form.start_number).trim();
  const autoStart = startRaw === '';
  const start = autoStart ? 1 : (parseInt(startRaw, 10) || 0);
  const sample = (() => {
    if (qty <= 0) return [];
    if (autoStart) {
      return [`${form.prefix}…`, `(${qty} units, numbered automatically)`];
    }
    const nums = [];
    for (let i = 0; i < Math.min(qty, 3); i += 1) {
      nums.push(`${form.prefix}${start + i}`);
    }
    const last = `${form.prefix}${start + qty - 1}`;
    return qty <= 4
      ? Array.from({ length: qty }, (_, i) => `${form.prefix}${start + i}`)
      : [...nums, '…', last];
  })();

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    if (noTypesDefined) {
      setError('Define a unit type first.');
      return;
    }
    if (!form.unit_type_id) {
      setError('Pick a unit type.');
      return;
    }

    setLoading(true);
    try {
      const rateRaw = String(form.monthly_rate).trim();
      const rateNum = rateRaw === '' ? null : parseFloat(rateRaw);

      const res = await apiClient.post(
        `/api/facilities/${facilityId}/units/bulk`,
        {
          prefix: form.prefix,
          start_number: autoStart ? null : start,
          quantity: qty,
          unit_type_id: form.unit_type_id,
          unit_type: form.unit_type,
          unit_category: form.unit_category,
          // Blank = inherit from catalog on the backend; non-blank = batch override.
          monthly_rate:
            rateNum != null && !Number.isNaN(rateNum) ? rateNum : null,
          // Width + length intentionally omitted -- they live on
          // unit_type and the backend resolves on read via the rate
          // resolver pattern.
          notes: form.notes || null,
        }
      );
      const n = res.data?.created ?? qty;
      navigate('/units', { state: { flash: `${n} units created.` } });
    } catch (err) {
      setError(
        err?.response?.data?.detail || 'Failed to create units.'
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:bg-slate-50 disabled:text-slate-400';
  const labelClass = 'block text-[13px] font-medium text-slate-700 mb-1.5';

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Layers className="h-5 w-5 text-slate-400" /> Bulk Add Units
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Generate many units of one type at once. You can rename any
          unit afterward.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-[13px] text-red-600 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {noTypesDefined && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
          <p className="font-medium mb-1">Define unit types first</p>
          <p className="text-amber-700">
            This facility has no unit types defined yet. Unit types are
            the rate sheet — each unit inherits its monthly rate from
            its type.{' '}
            <Link
              to="/units?view=types"
              className="font-medium underline hover:text-amber-900"
            >
              Manage unit types →
            </Link>
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white p-5">
        <form onSubmit={submit} className="space-y-5">
          <fieldset disabled={noTypesDefined} className="space-y-5">
            <div>
              <p className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Numbering
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>
                    Prefix <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text" name="prefix" value={form.prefix}
                    onChange={set} className={inputClass}
                    placeholder="A — blank = numbers only"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Start Number <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="number" name="start_number" min="0"
                    value={form.start_number} onChange={set}
                    className={inputClass}
                    placeholder="auto-continues if blank"
                  />
                </div>
                <div>
                  <label className={labelClass}>Quantity *</label>
                  <input
                    type="number" name="quantity" required min="1" max="500"
                    value={form.quantity} onChange={set}
                    className={inputClass}
                  />
                </div>
              </div>
              {qty > 0 && (
                <p className="text-[12px] text-slate-500 mt-2">
                  Will create <span className="font-semibold text-slate-700">{qty}</span> units
                  {autoStart ? (
                    <> — numbered automatically (you can rename any unit after).</>
                  ) : (
                    <>: <span className="font-mono text-slate-700">{sample.join(', ')}</span></>
                  )}
                </p>
              )}
            </div>

            <div>
              <p className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Unit Type
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Type *</label>
                  <select
                    name="unit_type_id" required
                    value={form.unit_type_id}
                    onChange={handleTypeChange}
                    className={inputClass}
                  >
                    <option value="">
                      {loadingTypes ? 'Loading types…' : '— select a unit type —'}
                    </option>
                    {unitTypes.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.name} — ${Number(t.monthly_rate).toFixed(2)}/mo
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Category</label>
                  <select
                    name="unit_category" value={form.unit_category}
                    onChange={set} className={inputClass}
                  >
                    <option value="enclosed">Enclosed</option>
                    <option value="climate_controlled">Climate controlled</option>
                    <option value="outdoor_parking">Outdoor parking</option>
                    <option value="covered_canopy">Covered canopy</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>
                    Monthly Rate{' '}
                    <span className="text-slate-400 font-normal">
                      (defaults to type rate)
                    </span>
                  </label>
                  <input
                    type="number" name="monthly_rate" step="0.01"
                    value={form.monthly_rate} onChange={set}
                    className={inputClass} placeholder="inherit from type"
                  />
                </div>
                {/* Width + Length removed from bulk-add 2026-06-01 --
                    dimensions live on the unit_type, no reason to retype
                    per batch. Backend resolves them from the selected
                    unit_type when rendering / displaying. */}
              </div>
            </div>

            <div>
              <label className={labelClass}>Notes</label>
              <textarea
                name="notes" value={form.notes} onChange={set} rows="2"
                className={inputClass} placeholder="Applied to all units in this batch"
              />
            </div>
          </fieldset>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button" onClick={() => navigate('/units')}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors"
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button
              type="submit" disabled={loading || qty < 1 || noTypesDefined}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-3.5 w-3.5" />
              {loading ? 'Creating…' : `Create ${qty} Units`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnitBulkForm;
